import React, { useCallback } from "react";
import Joyride, { CallBackProps, EVENTS, STATUS, ACTIONS } from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useTour } from "./TourContext";
import CustomBeacon from "./CustomBeacon";
import CustomTooltip from "./CustomTooltip";

const GlobalTour = () => {
  const { steps, stepIndex, run, setStepIndex, setRun, setShouldOpenModal,setShouldCloseModal } = useTour();
  const navigate = useNavigate();

  // helper: wait for selector (MutationObserver + timeout)
  const waitForSelector = (selector?: string, timeout = 5000) =>
    new Promise<boolean>((resolve) => {
      if (!selector) return resolve(false);
      if (document.querySelector(selector)) return resolve(true);

      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          observer.disconnect();
          resolve(true);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      setTimeout(() => {
        try { observer.disconnect(); } catch {}
        resolve(false);
      }, timeout);
    });

const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status, index, action, step, type } = data;
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setStepIndex(0);
        setRun(false);
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        // ✅ Handle NEXT
        if (action === ACTIONS.NEXT) {
          // --- Modal handling ---
          if (step?.data?.nextOpenModal) {
            setShouldOpenModal(true); // open modal
            const nextStep = step.data.stepNumber ?? index + 1;
            waitForSelector(steps[nextStep]?.target as string | undefined, 6000).then(() => {
              setStepIndex(nextStep);
            });
            return;
          }

          // --- Navigation handling ---
          if (step?.data?.next) {
            navigate(step.data.next, {
              state: { resumeTourFromStep: index + 1 },
            });
            return;
          }

          // --- Default NEXT ---
          setStepIndex(index + 1);
          return;
        }

        // ✅ Handle PREV
        if (action === ACTIONS.PREV) {

            // --- Close modal & go back ---
        if (step?.data?.backCloseModal) {
            setShouldCloseModal(true); // close modal
            const prevStepIndex = step.data.previousStep ?? index - 1;
            waitForSelector(steps[prevStepIndex]?.target as string | undefined, 6000).then(() => {
              setStepIndex(prevStepIndex);
            });
            return;
          }

          if (step?.data?.previous) {
            // --- Going back into modal ---
            if (step?.data?.backToModal) {
                setShouldOpenModal(true); // re-open modal
                const prevStepIndex = step.data.previousStep ?? index - 1;
                waitForSelector(steps[prevStepIndex]?.target as string | undefined, 6000).then(() => {
                  setStepIndex(prevStepIndex);
                });
              }
            const previousStepIndex = step.data.previousStep ?? index - 1;
            navigate(step.data.previous, {
              state: { resumeTourFromStep: previousStepIndex },
            });
            return;
          }

          setStepIndex(index - 1);
          return;
        }
      }
    },
    [navigate, setStepIndex, setRun, setShouldOpenModal, steps]
  );

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      disableCloseOnEsc
      disableOverlayClose
      scrollToFirstStep
      showProgress
      showSkipButton={false}
      hideCloseButton
      hideBackButton={false}
      tooltipComponent={CustomTooltip}
      beaconComponent={CustomBeacon}
      callback={handleCallback}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: "#28a745",
          backgroundColor: "#fff",
          textColor: "#333",
          arrowColor: "#fff",
        },
        overlay: {
          zIndex: 9999,
        },
      }}
    />
  );
};

export default GlobalTour;

